// Single source of truth for "what price should this product line default
// to" across every staff-facing order entry point (POS, the staff Order
// Form, the order drawer, the JSON import). Mirrors cps-ecom/lib/utils.ts
// — a thin pass-through over the backend's already-computed effective_price
// rather than re-deriving the sale date-window logic client-side, so this
// never disagrees with what the server would charge. Always returns a
// plain, still-editable number: staff can override it afterward for a
// manual discount exactly like before this existed.

interface PricedProduct {
    selling_price: number;
    effective_price?: number;
    /** Combo prices: from `min_quantity` units, each costs `unit_price`. */
    price_tiers?: { min_quantity: number; unit_price: number }[] | null;
}

export interface ComboTier {
    min_quantity: number;
    unit_price: number;
}

const toMoney = (value: number): number => Math.round(value * 100) / 100;

/** Equal to the paisa — prices arrive from the API as text. */
export const samePrice = (a: number, b: number): boolean => Math.round(a * 100) === Math.round(b * 100);

/** Today's price for one unit: the sale or campaign price while one runs. */
export function getEffectivePrice(product: Pick<PricedProduct, "selling_price" | "effective_price">): number {
    return Number(product.effective_price ?? product.selling_price) || 0;
}

// Combo prices ("buy 4 or more → ৳250 each"). Reaching a tier lowers the
// price of every unit in the line; a sale or campaign price that is already
// lower still wins. The same rule as the API's Product.unit_price_for and
// the shop's getUnitPriceFor — change them together.

/** The combo prices that beat today's price, smallest quantity first. */
export function getComboTiers(product: PricedProduct): ComboTier[] {
    const today = getEffectivePrice(product);
    return (product.price_tiers ?? [])
        .map((t) => ({ min_quantity: Number(t.min_quantity), unit_price: Number(t.unit_price) }))
        .filter((t) => t.unit_price < today)
        .sort((a, b) => a.min_quantity - b.min_quantity);
}

/** What one unit costs when `quantity` are bought together. */
export function unitPriceFor(product: PricedProduct, quantity: number): number {
    const reached = getComboTiers(product).filter((t) => t.min_quantity <= quantity);
    return reached.length ? reached[reached.length - 1].unit_price : getEffectivePrice(product);
}

/** The next combo price up from `quantity`, and how many more it takes. */
export function nextComboTier(product: PricedProduct, quantity: number): { tier: ComboTier; more: number } | null {
    const tier = getComboTiers(product).find((t) => t.min_quantity > quantity);
    return tier ? { tier, more: tier.min_quantity - quantity } : null;
}

/** What the combo price saves on `quantity` units, against today's price. */
export function comboSaving(product: PricedProduct, quantity: number): number {
    return toMoney((getEffectivePrice(product) - unitPriceFor(product, quantity)) * quantity);
}

/**
 * The line's new unit price after its quantity changes from `fromQty` to
 * `toQty` — or null to leave it. A line still on its automatic price follows
 * the combo prices; one whose price staff typed in keeps it.
 */
export function repriceForQuantity(
    product: PricedProduct,
    currentPrice: number,
    fromQty: number,
    toQty: number,
): number | null {
    if (!samePrice(Number(currentPrice), unitPriceFor(product, fromQty))) return null;
    const next = unitPriceFor(product, toQty);
    return samePrice(next, Number(currentPrice)) ? null : next;
}
